import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { assertNotNull, AsyncResult, Maybe } from '../core';
import { Store } from '../store/store';
import { BindingProps, StoreNode } from '../store';
import { ApiResult, ApiVariables } from '../api/apiSchema';
import { GetInvitationsFilterInput, InvitationByOption, Order, PageInfo, TeamMemberRole, TeamMembersByOption } from '@clipr/lib';
import { SyncStatus } from '../store/syncSchema';
import last from 'lodash/last';
import debounce from 'lodash/debounce';
import { input, InputState, SortFieldValue } from '../components';
import { TeamInviteCatalogItem } from './teamInviteCatalogItem';

type Props = BindingProps<{
  teamId: string,
  pageSize?: number
}>

export class TeamInviteCatalogSource
  extends StoreNode {

  readonly nodeType: 'TeamInviteCatalogSource' = 'TeamInviteCatalogSource';

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable(this);

    this.searchInput = input(this, {
      name: 'searchInput',
      onChange: (e) => this.handleSearchInputChange(e),
    });
  }

  readonly searchInput: InputState;

  // #region Resolved props
  // -------

  @computed get teamId(): string {
    return this.resolvedProps.teamId;
  }

  @computed get pageSize(): number {
    return this.resolvedProps.pageSize || 20;
  }

  // #endregion

  @computed get team() {
    return this.store.teamManager.strictGetTeam(this.teamId);
  }

  @computed get isLoading(): boolean {
    return this.syncStatus === 'fetching';
  }

  @computed get isFirstRequest(): boolean {
    return !this.lastItemCursor;
  }

  @computed get isLastRequest(): boolean {
    const totalCount = (this.team && this.team.invitationsCount) || 0;
    return !this.isEndOfList && (totalCount - this.items.length <= this.pageSize);
  }

  readonly items = observable.array<TeamInviteCatalogItem>();

  @observable syncStatus: SyncStatus = 'idle';
  @observable pageInfo: PageInfo | null = null;
  @observable sortField: InvitationByOption | null = null;
  @observable sortOrder: Order | null = null;
  @observable roleFilter: TeamMemberRole | null = null;
  @observable searchFilter: string | null = null;

  @computed get lastItemCursor(): string | null {
    return last(this.items)?.cursor || null;
  }

  @computed get isEndOfList(): boolean {
    return this.pageInfo ? !this.pageInfo.hasNextPage : false;
  }

  /** Makes a fresh fetch call, clearing any previous data and pagination state. */
  @action
  async fetch()
    : AsyncResult<TeamInviteCatalogItem[]> {

    this.syncStatus = 'fetching';

    const [invitationsRes, err] = await this.fetchRaw();

    if (err)
      return [null, err];

    assertNotNull(invitationsRes);

    const retItems: TeamInviteCatalogItem[] = [];
    runInAction(() => {
      this.items.clear();

      const { getTeamInvitations } = invitationsRes;
      this.pageInfo = getTeamInvitations.pageInfo;

      getTeamInvitations.edges.forEach((edge, i) => {

        this.team.insertInvitation(edge.node);

        const item = new TeamInviteCatalogItem(this.store, {
          teamId: this.teamId,
          email: edge.node.email,
          role: edge.node.role,
          cursor: edge.cursor,
          index: i
        });

        this.items.push(item);
        retItems.push(item);
      });
    });

    return [retItems];
  }

  @action
  async fetchMore()
    : AsyncResult<TeamInviteCatalogItem[]> {

    this.syncStatus = 'fetchingMore';

    const [membersRes, err] = await this.fetchRaw(true);
    if (err)
      return [null, err];

    assertNotNull(membersRes);

    const retItems: TeamInviteCatalogItem[] = [];
    runInAction(() => {
      const { getTeamInvitations } = membersRes;
      this.pageInfo = getTeamInvitations.pageInfo;

      getTeamInvitations.edges.forEach((edge, i) => {

        this.team.insertInvitation(edge.node);

        const item = new TeamInviteCatalogItem(this.store, {
          teamId: this.teamId,
          email: edge.node.email,
          role: edge.node.role,
          cursor: edge.cursor,
          index: i
        });

        this.items.push(item);
        retItems.push(item);
      });
    });
    return [retItems];
  }

  getSortFieldValue(field: InvitationByOption) {
    if (this.sortField !== field)
      return 'none';
    return this.sortOrder!;
  }

  @action
  setSortFieldValue<T extends InvitationByOption | TeamMembersByOption>(field: T, val?: SortFieldValue, fetch: boolean = true) {
    if (!val || val === 'none') {
      this.sortField = null;
      this.sortOrder = null;
    } else {
      this.sortField = field as InvitationByOption;
      this.sortOrder = val;
    }
    if (fetch) this.fetchDebounced();
  }

  @action
  setFilterFieldValue<T extends keyof GetInvitationsFilterInput>(field: T, val?: Maybe<GetInvitationsFilterInput[T]>) {
    switch (field) {
      case 'role':
        if (val === this.roleFilter) {
          this.roleFilter = null;
        } else {
          this.roleFilter = val as TeamMemberRole || null;
        }
        break;
    }
    this.fetchDebounced();
  }

  getFilterFieldValue<T extends keyof GetInvitationsFilterInput>(field: T): GetInvitationsFilterInput[T] | null {
    switch (field) {
      case 'role':
        return this.roleFilter;
    }
    return null;
  }

  @action
  handleSearchInputChange(evt: InputState) {
    this.searchFilter = evt.value || null;
    this.fetchDebounced();
  }

  @action
  private getFetchArgs(more: boolean = false) {
    const args: ApiVariables<'getTeamInvitations'> = {
      id: this.teamId
    };
    args.first = this.pageSize;

    if (more && this.lastItemCursor)
      args.after = this.lastItemCursor;

    if (this.sortField && this.sortOrder) {
      args.sort = {
        field: this.sortField,
        order: this.sortOrder
      }
    }

    const filter: GetInvitationsFilterInput = {};

    if (this.roleFilter) {
      filter.role = this.roleFilter;
    }

    if (this.searchFilter) {
      filter._search = this.searchFilter || undefined;
    }

    args.filter = filter;

    return args;
  }

  fetchDebounced = debounce(() => this.fetch(), 500);

  @action
  private async fetchRaw(more: boolean = false): ApiResult<'getTeamInvitations'> {

    const args: ApiVariables<'getTeamInvitations'> = this.getFetchArgs(more);

    const [teamInvitations, err] = await this.store.api.getTeamInvitations({ ...args, id: this.teamId });
    if (err) {
      runInAction(() => {
        this.syncStatus = 'error';
      });
      return [null, err];
    }

    runInAction(() => {
      this.syncStatus = 'idle';
    });

    assertNotNull(teamInvitations);
    return [teamInvitations];
  }

  @action
  resetSearchField() {
    this.searchFilter = null;
    this.searchInput.loadValue(null);
  }

  @action
  reset() {
    this.sortField = null;
    this.sortOrder = null;
    this.roleFilter = null;
    this.searchFilter = null;
    this.searchInput.loadValue(null);
    this.items.clear();
  }
}
